// Shared procedural drawing helpers. We don't ship sprite assets in MVP —
// everything is drawn with Phaser.Graphics so the project stays asset-light
// and the placeholder art looks intentional rather than missing.

import Phaser from 'phaser';

export const COLORS = {
  bg: 0x1b1530,
  bgFar: 0x100a22,
  panel: 0x2a1f50,
  accent: 0x6c4ad6,
  accent2: 0x4d31a8,
  hero: 0x6c4ad6,
  heroHat: 0x3a2d6e,
  gold: 0xd6b04a,
  good: 0x4dd672,
  bad: 0xd6634d,
  text: 0xf5efff,
} as const;

// Stylized wizard avatar drawn at (x,y) facing right.
export function drawWizard(g: Phaser.GameObjects.Graphics, x: number, y: number, scale = 1): void {
  const s = scale;
  // Robe
  g.fillStyle(COLORS.hero, 1);
  g.fillTriangle(x - 24 * s, y + 30 * s, x + 24 * s, y + 30 * s, x, y - 16 * s);
  // Head
  g.fillStyle(0xfde6c2, 1);
  g.fillCircle(x, y - 24 * s, 12 * s);
  // Hat
  g.fillStyle(COLORS.heroHat, 1);
  g.fillTriangle(x - 16 * s, y - 30 * s, x + 16 * s, y - 30 * s, x + 2 * s, y - 64 * s);
  g.fillStyle(COLORS.gold, 1);
  g.fillCircle(x + 2 * s, y - 64 * s, 3 * s);
  // Wand
  g.lineStyle(3 * s, COLORS.gold, 1);
  g.beginPath();
  g.moveTo(x + 18 * s, y - 4 * s);
  g.lineTo(x + 40 * s, y - 28 * s);
  g.strokePath();
  g.fillStyle(0xffe27a, 1);
  g.fillCircle(x + 40 * s, y - 28 * s, 4 * s);
}

export function drawMonster(g: Phaser.GameObjects.Graphics, x: number, y: number, kind: number, scale = 1): void {
  const s = scale;
  // Palette per kind so each monster type looks distinct.
  const palette = [
    { body: 0x4dd672, eye: 0x14102a }, // green slime
    { body: 0xd6634d, eye: 0x14102a }, // red imp
    { body: 0x4a9bd6, eye: 0xf5efff }, // blue beast
    { body: 0xd6a04d, eye: 0x14102a }, // orange tiger
    { body: 0x9b8db5, eye: 0xf5efff }, // grey wolf
    { body: 0xa54dd6, eye: 0xffe27a }, // purple wraith
  ][kind % 6];

  // Body (rounded blob)
  g.fillStyle(palette.body, 1);
  g.fillEllipse(x, y, 64 * s, 56 * s);
  g.fillTriangle(x - 24 * s, y + 14 * s, x - 30 * s, y + 28 * s, x - 8 * s, y + 24 * s);
  g.fillTriangle(x + 24 * s, y + 14 * s, x + 30 * s, y + 28 * s, x + 8 * s, y + 24 * s);
  // Eyes
  g.fillStyle(0xffffff, 1);
  g.fillCircle(x - 10 * s, y - 4 * s, 8 * s);
  g.fillCircle(x + 10 * s, y - 4 * s, 8 * s);
  g.fillStyle(palette.eye, 1);
  g.fillCircle(x - 10 * s, y - 4 * s, 4 * s);
  g.fillCircle(x + 10 * s, y - 4 * s, 4 * s);
  // Mouth
  g.lineStyle(2 * s, 0x14102a, 1);
  g.beginPath();
  g.moveTo(x - 8 * s, y + 12 * s);
  g.lineTo(x + 8 * s, y + 12 * s);
  g.strokePath();
}

const MONSTER_EMOJI = ['🟢', '👹', '🐲', '🐯', '🐺', '🟣'];
const MONSTER_NAMES = ['Slime', 'Imp', 'Beast', 'Tiger Cub', 'Wolfling', 'Wraith'];

export function monsterMeta(kind: number) {
  return {
    emoji: MONSTER_EMOJI[kind % MONSTER_EMOJI.length],
    name: MONSTER_NAMES[kind % MONSTER_NAMES.length],
  };
}

export function drawParallaxBg(scene: Phaser.Scene, themeColor: number = COLORS.bg): Phaser.GameObjects.Graphics {
  const { width, height } = scene.scale;
  const g = scene.add.graphics();
  g.fillGradientStyle(themeColor, themeColor, COLORS.bgFar, COLORS.bgFar, 1);
  g.fillRect(0, 0, width, height);
  // Distant hills
  g.fillStyle(0x251b48, 1);
  for (let i = 0; i < 5; i++) {
    const cx = (i * 220) - 100;
    g.fillCircle(cx, height * 0.7, 180);
  }
  g.fillStyle(0x1b1530, 1);
  g.fillRect(0, height * 0.78, width, height * 0.22);
  return g;
}
